import {
  Archive,
  ArrowRight,
  BriefcaseBusiness,
  FilePlus2,
  Files,
  RotateCcw,
} from "lucide-react";
import { encodeCuijPath } from "@tribunalea/shared";
import "./praxis-justiniano.css";

function workspaceLabel(workspaceType) {
  return workspaceType === "organization"
    ? "la organización activa"
    : "tu espacio personal";
}

function ExpedienteCard({ expediente, navigate }) {
  return (
    <button
      type="button"
      className="praxis-justiniano-expediente"
      onClick={() =>
        navigate(`/expedientes/${encodeCuijPath(expediente.cuij)}`)
      }
    >
      <span className="praxis-justiniano-file-icon">
        <Files size={16} />
      </span>
      <span className="praxis-justiniano-expediente-copy">
        <strong>{expediente.cuij}</strong>
        <span>{expediente.caratula || "Sin carátula informada"}</span>
        {expediente.matchedOn?.length > 0 && (
          <small>Coincide en {expediente.matchedOn.join(", ")}</small>
        )}
      </span>
      <ArrowRight size={15} />
    </button>
  );
}

function ExpedienteCollection({ expedientes, navigate }) {
  if (!expedientes?.length) return null;

  return (
    <div className="praxis-justiniano-results">
      {expedientes.slice(0, 6).map((expediente) => (
        <ExpedienteCard
          key={expediente.cuij}
          expediente={expediente}
          navigate={navigate}
        />
      ))}
      {expedientes.length > 6 && (
        <small className="praxis-justiniano-more">
          Hay {expedientes.length - 6} resultados más.
        </small>
      )}
    </div>
  );
}

function ExpedienteDetail({ expediente, navigate }) {
  if (!expediente?.cuij) return null;

  return (
    <div className="praxis-justiniano-detail">
      <div className="praxis-justiniano-detail-heading">
        <span className="praxis-justiniano-file-icon">
          <BriefcaseBusiness size={16} />
        </span>
        <span>
          <strong>{expediente.cuij}</strong>
          <small>
            {expediente.isArchived
              ? "Archivado"
              : expediente.isSaved
                ? "En Mis expedientes"
                : "Consulta judicial"}
          </small>
        </span>
      </div>
      <p>{expediente.caratula || "Sin carátula informada"}</p>
      <dl>
        {expediente.organismoActual && (
          <div>
            <dt>Organismo</dt>
            <dd>{expediente.organismoActual}</dd>
          </div>
        )}
        <div>
          <dt>Actuaciones</dt>
          <dd>{expediente.actuacionesCount || 0}</dd>
        </div>
      </dl>
      <button
        type="button"
        onClick={() =>
          navigate(`/expedientes/${encodeCuijPath(expediente.cuij)}`)
        }
      >
        Ver expediente completo <ArrowRight size={14} />
      </button>
    </div>
  );
}

const actionPresentation = {
  "praxis.expedientes.add": {
    icon: FilePlus2,
    title: "Agregar a Mis expedientes",
    description: "Praxis lo guardará y lo incluirá en las actualizaciones.",
  },
  "praxis.expedientes.archive": {
    icon: Archive,
    title: "Archivar expediente",
    description:
      "Se ocultará de la lista activa, pero seguirá actualizándose y conservará sus datos.",
  },
  "praxis.expedientes.restore": {
    icon: RotateCcw,
    title: "Reactivar expediente",
    description: "Volverá a mostrarse en la lista activa de Mis expedientes.",
  },
};

const toolStatuses = {
  "praxis.expedientes.search": "Buscando coincidencias en tus expedientes…",
  "praxis.expedientes.list": "Consultando tus expedientes…",
  "praxis.expedientes.get": "Revisando los datos del expediente…",
  "praxis.expedientes.timeline.list":
    "Leyendo actuaciones y movimientos recientes…",
  "praxis.expedientes.timeline.search":
    "Buscando dentro del historial del expediente…",
  "praxis.expedientes.add": "Preparando el expediente para agregarlo…",
  "praxis.expedientes.archive": "Preparando el archivo del expediente…",
  "praxis.expedientes.restore": "Preparando la reactivación del expediente…",
};

function PraxisConfirmation({ action }) {
  const presentation = actionPresentation[action.toolName];
  if (!presentation) return null;
  const Icon = presentation.icon;

  return (
    <div className="praxis-justiniano-confirm-copy">
      <span>
        <Icon size={16} />
      </span>
      <div>
        <strong>{presentation.title}</strong>
        <p>{action.input?.cuij}</p>
        <small>
          {presentation.description} Se ejecutará en{" "}
          {workspaceLabel(action.workspaceType)}.
        </small>
      </div>
    </div>
  );
}

export const praxisJustinianoRenderers = {
  getToolStatus({ toolName }) {
    return toolStatuses[toolName] || "Consultando información de Praxis…";
  },

  renderToolResult({ toolName, data, context }) {
    if (
      toolName === "praxis.expedientes.search" ||
      toolName === "praxis.expedientes.list"
    ) {
      return (
        <ExpedienteCollection
          expedientes={data}
          navigate={context.navigate}
        />
      );
    }
    if (
      [
        "praxis.expedientes.get",
        "praxis.expedientes.add",
        "praxis.expedientes.archive",
        "praxis.expedientes.restore",
      ].includes(toolName)
    ) {
      return <ExpedienteDetail expediente={data} navigate={context.navigate} />;
    }
    return null;
  },

  renderConfirmation({ action }) {
    return <PraxisConfirmation action={action} />;
  },
};
